import { NavLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  LayoutDashboard,
  History,
  FolderOpen,
  LogOut,
  X,
} from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { redirectToSSOLogin } from '@/lib/sso'

interface AppSidebarProps {
  isOpen: boolean
  onClose: () => void
}

const navItems = [
  { label: 'Tổng quan', icon: LayoutDashboard, path: '/', end: true },
  { label: 'Lịch sử', icon: History, path: '/transactions' },
  { label: 'Danh mục', icon: FolderOpen, path: '/portfolios' },
]

export const AppSidebar: React.FC<AppSidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth()

  const handleLogout = async () => {
    await logout()
    redirectToSSOLogin(window.location.origin)
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:relative lg:translate-x-0 lg:z-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-2" onClick={onClose}>
            <Icon icon="ph:wallet-fill" className="w-7 h-7 text-blue-600" />
            <span className="text-lg font-bold text-gray-900">Quản lý Tài sản</span>
          </NavLink>
          <button onClick={onClose} className="lg:hidden p-1 rounded-md hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {item.label}
            </NavLink>
          ))}

        </nav>

        {/* User info + logout */}
        <div className="px-3 py-4 border-t border-gray-200">
          {user && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt="Avatar"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-white">
                    {(user.display_name || user.email).charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.display_name || user.email}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            Đăng xuất
          </button>
        </div>
      </aside>
    </>
  )
}
